import React, { useState, useEffect, useRef } from 'react';
import { Camera, MapPin, CheckCircle, AlertCircle, Users, Clock, Shield, X, Sparkles, Info, QrCode, FileSignature, Monitor, Filter } from 'lucide-react';
import * as faceapi from 'face-api.js';
import { api } from '../utils/api';
import { getCurrentLocation, getCameraConstraints } from '../utils/helpers';

// [✅ แก้ไข] ลบ { modelsLoaded } ออกจาก props เพราะเราจะโหลดข้างในนี้แทน
  const FaceScanner = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const captureCanvasRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [scanError, setScanError] = useState(null);
  const [location, setLocation] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [isManualStop, setIsManualStop] = useState(false);
  const streamRef = useRef(null);

  const [modelsLoaded, setModelsLoaded] = useState(false);
  
  const [currentTime, setCurrentTime] = useState(new Date()); 
  
  const [wfhMode, setWfhMode] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [showVersionModal, setShowVersionModal] = useState(false); 
  const [isWarmingUp, setIsWarmingUp] = useState(false);
  const CURRENT_VERSION = 'v1.1.3';

// [✅ อัปเดต] รวบการโหลด Model และการ Warm-up ไว้ในคราวเดียวกัน เพื่อไม่ให้ UI กระตุก
  useEffect(() => {
    const initAI = async () => {
      const MODEL_URL = 'https://justadudewhohacks.github.io/face-api.js/models';
      
      try {
        console.log('1. Loading Face API models...');
        await Promise.all([
          faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
          faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL)
        ]);
        
        // พอโหลดเสร็จ เปลี่ยนสถานะให้ข้อความหน้าจอเปลี่ยน แต่ไม่ต้องหยุดวงแหวนหมุน
        setModelsLoaded(true);
        setIsWarmingUp(true); 

        console.log('2. Models loaded. Starting AI Warm-up...');
        
        const dummyCanvas = document.createElement('canvas');
        dummyCanvas.width = 10;
        dummyCanvas.height = 10;
        
        await faceapi
          .detectSingleFace(dummyCanvas, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.5 }))
          .withFaceLandmarks()
          .withFaceDescriptor();
          
        console.log('3. AI Warm-up complete. System Ready.');
        
        // เสร็จสมบูรณ์ทุกขั้นตอน ค่อยปิดสถานะ Warm-up
        setIsWarmingUp(false);
        
      } catch (error) {
        console.error('Error initializing AI:', error);
        alert('ไม่สามารถโหลด AI Model ได้ กรุณาตรวจสอบอินเทอร์เน็ต');
      }
    };

    initAI();
  }, []);

  useEffect(() => {
    loadCurrentLocation();
    checkWfhMode();
  }, []);

  // [✅ อัปเดต] ปิดกล้องชัวร์ๆ เมื่อเปลี่ยนไปหน้าอื่น (แก้ปัญหาไฟค้างบน iOS)
  useEffect(() => {
    // ต้องเก็บค่า ref ปัจจุบันไว้ในตัวแปรก่อน เพราะตอนเปลี่ยนหน้า (Unmount) ตัววิดีโออาจจะถูกทำลายไปก่อน
    const videoElement = videoRef.current;

    return () => {
      // 1. ตัดสายสัญญาณ (เหมือนเดิม)
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      // 2. [เพิ่มใหม่] สั่งหยุดและล้างค่าแท็ก Video (ไม้ตายสำหรับ iOS)
      if (videoElement) {
        videoElement.pause();             // สั่งหยุดเล่น
        videoElement.srcObject = null;    // ถอด Stream ออกจาก Video
        videoElement.removeAttribute('src'); // ล้างค่า src (กันเหนียว)
        videoElement.load();              // บังคับให้เบราว์เซอร์รีเซ็ตแท็กนี้ใหม่
      }
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // AI Warm-up (ทำงานเมื่อ modelsLoaded เป็น true)
  useEffect(() => {
    const warmUpAI = async () => {
      if (modelsLoaded && !isWarmingUp) {
        setIsWarmingUp(true);
        console.log('AI Warm-up started...');
        
        try {
          const dummyCanvas = document.createElement('canvas');
          dummyCanvas.width = 10;
          dummyCanvas.height = 10;
          
          await faceapi
            .detectSingleFace(dummyCanvas, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.5 }))
            .withFaceLandmarks()
            .withFaceDescriptor();

        } catch (error) {
          console.error('AI Warm-up failed:', error);
        }
        
        setIsWarmingUp(false);
      }
    };
    
    warmUpAI();
  }, [modelsLoaded]); // จะทำงานเมื่อโหลดโมเดลเสร็จ

  // [✅ อัปเดต] ตรวจสอบเงื่อนไขและเปิดกล้องอัตโนมัติ
  useEffect(() => {
    // เพิ่ม && !isManualStop เพื่อไม่ให้มันดึงกล้องกลับมาเปิดเองถ้าเราเพิ่งกดปิดไป
    if (modelsLoaded && !isWarmingUp && !cameraActive && !isManualStop) {
      if (wfhMode) {
        startCamera();
      } else if (location && !location.loading && !location.error && location.accuracy <= 100) {
        startCamera();
      }
    }
  }, [modelsLoaded, isWarmingUp, cameraActive, wfhMode, location, isManualStop]); // อย่าลืมเติม isManualStop ตรงนี้

  // [✅ เพิ่มใหม่] ฟังก์ชันสแกนใบหน้าแบบเบื้องหลัง (ไม่โชว์ Alert ถ้าไม่เจอหน้า)
  const autoDetectFace = async () => {
    // ถ้ากำลังประมวลผลอยู่ หรือเปิด Modal ยืนยันอยู่ ให้ข้ามไปก่อน
    if (!videoRef.current || isScanning || pendingConfirmation || result) return;

    try {
      const video = videoRef.current;
      
      // เช็คว่าวิดีโอเล่นอยู่จริงๆ
      if (video.videoWidth === 0 || video.videoHeight === 0) return;

      const detection = await faceapi
        .detectSingleFace(video, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.6 })) // เพิ่มความมั่นใจขั้นต่ำ ป้องกันหน้าหลอน
        .withFaceLandmarks()
        .withFaceDescriptor();
      
      // ถ้าเจอหน้าคน ให้เริ่มกระบวนการระบุตัวตน
      if (detection) {
        setIsScanning(true); // ขึ้น Loading หน้าจอ
        
        const canvas = canvasRef.current;
        const displaySize = { width: video.videoWidth, height: video.videoHeight };
        faceapi.matchDimensions(canvas, displaySize);
        
        const capturedImage = captureImage();
        await identifyPerson(Array.from(detection.descriptor), capturedImage);
      }
    } catch (error) {
      console.error('Auto detect error:', error);
      // ปล่อยผ่านไปเงียบๆ เพื่อให้รอบต่อไปทำงานต่อได้
    }
  };

  // [✅ เพิ่มใหม่] Loop สแกนหน้าทุกๆ 1.5 วินาทีเมื่อกล้องเปิด
  useEffect(() => {
    let scanInterval;
    
    if (cameraActive && !pendingConfirmation && !result && !isScanning) {
      scanInterval = setInterval(() => {
        autoDetectFace();
      }, 1500); // สแกนทุก 1.5 วินาที
    }

    return () => {
      if (scanInterval) clearInterval(scanInterval);
    };
  }, [cameraActive, pendingConfirmation, result, isScanning]);

  const checkWfhMode = async () => {
    try {
      const data = await api.getOrganizations();
      if (data.length > 0 && data[0].allow_wfh == 1) {
        setWfhMode(true);
      } else {
        setWfhMode(false);
      }
    } catch (error) {
      console.error('Error checking WFH mode:', error);
    }
  };

  const toggleVersionModal = () => {
      setShowVersionModal(prev => !prev);
  };
  
  const dismissVersionModal = () => {
      setShowVersionModal(false);
  };

  const loadCurrentLocation = async () => {
    setLocation({ loading: true });
    try {
      const locationData = await getCurrentLocation();
      setLocation({ ...locationData, loading: false });
    } catch (error) {
      console.error('Error getting location:', error);
      setLocation({ 
        loading: false, 
        error: true,
        message: 'ไม่สามารถรับตำแหน่งได้ กรุณาเปิด GPS'
      });
    }
  };

  const startCamera = async () => {
    if (!modelsLoaded || isWarmingUp) {
      alert('กำลังโหลดโมเดล AI กรุณารอสักครู่...');
      return;
    }

    if (!wfhMode) {
      if (!location || location.loading) {
        alert('กรุณารอระบบหาตำแหน่งของคุณให้เสร็จก่อน');
        if (!location) loadCurrentLocation();
        return;
      }

      if (location.error) {
        alert('ไม่สามารถรับตำแหน่ง GPS ได้\nกรุณา:\n1. เปิด GPS บนอุปกรณ์\n2. อนุญาตให้เบราว์เซอร์เข้าถึงตำแหน่ง\n3. ไปที่บริเวณโล่ง');
        loadCurrentLocation();
        return;
      }

      if (location.skipped || location.manual || location.latitude === 0) {
        alert('ระบบต้องการตำแหน่งที่แม่นยำเพื่อบันทึกเวลา กรุณาเปิด GPS');
        loadCurrentLocation();
        return;
      }

      if (location.accuracy && location.accuracy > 100) {
        alert(`ความแม่นยำของตำแหน่งต่ำเกินไป (±${Math.round(location.accuracy)}m)\n\nกรุณา:\n1. ไปที่บริเวณโล่ง\n2. รอให้สัญญาณ GPS ดีขึ้น\n3. ลองกดรีเฟรชตำแหน่ง`);
        return;
      }
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia(getCameraConstraints());
      streamRef.current = stream;

      if (!videoRef.current) {
        console.error('Video ref not found');
        return;
      }

      videoRef.current.srcObject = stream;
      setCameraActive(true);
      setResult(null);
      setPendingConfirmation(null);
      setIsManualStop(false);
      
    } catch (error) {
      console.error('Camera error:', error);
      
      let errorMessage = 'ไม่สามารถเข้าถึงกล้องได้';
      
      if (error.name === 'NotAllowedError') {
        errorMessage = 'กรุณาอนุญาตการใช้งานกล้องในเบราว์เซอร์:\n\n' +
                      '• Chrome: กดไอคอนกล้อง ข้างๆ URL แล้วเลือก "อนุญาต"\n' +
                      '• Safari: ตั้งค่า > กล้อง > อนุญาต\n' +
                      '• Firefox: กดไอคอนโล่ ข้างๆ URL';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'ไม่พบกล้อง กรุณาเชื่อมต่อกล้องกับอุปกรณ์';
      }
      
      alert(errorMessage);
    }
  };

  const stopCamera = () => {
    // 1. ตัดสายสัญญาณ
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    // 2. [เพิ่มใหม่] เคลียร์แท็ก Video สำหรับ iOS
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
      videoRef.current.removeAttribute('src');
      videoRef.current.load();
    }
    
    setCameraActive(false);
    setIsScanning(false);
    setIsManualStop(true); 
  };

  const captureImage = () => {
    const video = videoRef.current;
    const captureCanvas = captureCanvasRef.current;
    
    if (!video || !captureCanvas) return null;
    
    captureCanvas.width = video.videoWidth;
    captureCanvas.height = video.videoHeight;
    
    const ctx = captureCanvas.getContext('2d');
    ctx.save();
    ctx.scale(-1, 1);
    ctx.drawImage(video, -captureCanvas.width, 0, captureCanvas.width, captureCanvas.height);
    ctx.restore();
    
    const dataUrl = captureCanvas.toDataURL('image/jpeg', 0.7);
    return dataUrl;
  };

  const detectFace = async () => {
    if (!videoRef.current || isScanning) return;

    setIsScanning(true);
    
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const displaySize = { width: video.videoWidth, height: video.videoHeight };
      faceapi.matchDimensions(canvas, displaySize);
      
      const detection = await faceapi
        .detectSingleFace(video, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.5 }))
        .withFaceLandmarks()
        .withFaceDescriptor();
      
      if (detection) {
        const resizedDetection = faceapi.resizeResults(detection, displaySize);
        
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        const capturedImage = captureImage();
        
        await identifyPerson(Array.from(detection.descriptor), capturedImage);
      } else {
        alert('ไม่พบใบหน้าในกรอบ\n\nกรุณา:\n1. จัดท่าให้ใบหน้าอยู่ในกรอบ\n2. ถ่ายในที่มีแสงเพียงพอ\n3. มองตรงกล้อง');
        setIsScanning(false);
      }
      
    } catch (error) {
      console.error('Error detecting face:', error);
      alert('เกิดข้อผิดพลาดในการตรวจจับใบหน้า\nกรุณาลองใหม่อีกครั้ง');
      setIsScanning(false);
    }
  };

  const identifyPerson = async (faceDescriptor, capturedImage) => {
    try {
      const data = await api.identifyFace(faceDescriptor);
      
      if (data.success && data.employee) {
        const currentHour = new Date().getHours();
        const action = currentHour < 12 ? 'check_in' : 'check_out';
        
        setPendingConfirmation({
          employee: data.employee,
          faceDescriptor: faceDescriptor,
          capturedImage: capturedImage,
          action: action,
          confidence: data.confidence,
          timestamp: new Date().toISOString()
        });
        
        // ถ้าสำเร็จ ให้กล้องหยุดและรอการยืนยัน
        stopCamera();
        setIsScanning(false);
      } else {
        // [✅ ปรับแก้] ไม่พบใบหน้า: ไม่หยุดกล้อง แต่แสดง Error ชั่วคราว
        setScanError(data.message || 'ไม่พบใบหน้าในระบบ กำลังลองใหม่...');
        
        // หน่วงเวลา 3 วินาที ให้ผู้ใช้จัดท่าทาง ก่อนปลดล็อคให้สแกนรอบถัดไป
        setTimeout(() => {
          setScanError(null);
          setIsScanning(false);
        }, 3000); 
      }
      
    } catch (error) {
      console.error('Error identifying person:', error);
      // [✅ ปรับแก้] ถ้า API ล่มหรือเน็ตหลุด ให้แจ้งเตือนชั่วคราวเช่นกัน
      setScanError('เกิดข้อผิดพลาดในการตรวจสอบ กำลังลองใหม่...');
      
      setTimeout(() => {
        setScanError(null);
        setIsScanning(false);
      }, 3000);
    } 
    // หมายเหตุ: ลบ finally { setIsScanning(false); } อันเดิมออก 
    // เพราะเราย้ายไปควบคุมเวลาเปิด/ปิดใน setTimeout แทนแล้ว
  };

  const confirmAttendance = async () => {
    if (!pendingConfirmation) return;
    
    setIsScanning(true);
    
    try {
      const requestData = {
        face_descriptor: pendingConfirmation.faceDescriptor,
        location: wfhMode ? { wfh_mode: true, latitude: 0, longitude: 0 } : location,
        captured_image: pendingConfirmation.capturedImage,
        employee_id: pendingConfirmation.employee.employee_id,
        confirmed: true
      };

      const data = await api.recognizeFace(requestData);
      
      setResult(data);
      setPendingConfirmation(null);
      
    } catch (error) {
      console.error('Error processing attendance:', error);
      setResult({ 
        success: false, 
        error: error.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล'
      });
      setPendingConfirmation(null);
    } finally {
      setIsScanning(false);
    }
  };

  const cancelAndRescan = () => {
    setPendingConfirmation(null);
    setResult(null);
    startCamera();
  };

  const isSystemPreparing = !modelsLoaded || isWarmingUp || (!wfhMode && location?.loading);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">

      {pendingConfirmation && !result && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl animate-in zoom-in duration-300">
            
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 rounded-t-3xl">
              <div className="text-center">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 backdrop-blur-sm">
                  <Users className="h-10 w-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-1">ยืนยันตัวตน</h2>
                <p className="text-blue-100 text-sm">กรุณาตรวจสอบข้อมูลก่อนบันทึก</p>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-5">
                <div className="text-center mb-4">
                  <div className="inline-block bg-white rounded-2xl px-4 py-2 shadow-sm mb-3">
                    <p className="text-3xl font-bold text-gray-900">
                      {pendingConfirmation.employee.first_name} {pendingConfirmation.employee.last_name}
                    </p>
                  </div>
                  <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                    <span>{pendingConfirmation.employee.position}</span>
                    {pendingConfirmation.employee.department && (
                      <>
                        <span>•</span>
                        <span>{pendingConfirmation.employee.department}</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="bg-white rounded-xl p-3 flex items-center justify-between shadow-sm">
                    <div className="flex items-center space-x-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        pendingConfirmation.action === 'check_in' ? 'bg-green-100' : 'bg-orange-100'
                      }`}>
                        {pendingConfirmation.action === 'check_in' ? (
                          <span className="text-lg">🟢</span>
                        ) : (
                          <span className="text-lg">🟠</span>
                        )}
                      </div>
                      <span className="text-sm text-gray-600 font-medium">ประเภท</span>
                    </div>
                    <span className={`font-bold text-lg ${
                      pendingConfirmation.action === 'check_in' ? 'text-green-600' : 'text-orange-600'
                    }`}>
                      {pendingConfirmation.action === 'check_in' ? 'เข้างาน' : 'ออกงาน'}
                    </span>
                  </div>

                  <div className="bg-white rounded-xl p-3 flex items-center justify-between shadow-sm">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <Clock className="h-4 w-4 text-blue-600" />
                      </div>
                      <span className="text-sm text-gray-600 font-medium">เวลา</span>
                    </div>
                    <span className="font-bold text-gray-900">
                      {new Date().toLocaleTimeString('th-TH', {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      })}
                    </span>
                  </div>

                  {pendingConfirmation.confidence && (
                    <div className="bg-white rounded-xl p-3 flex items-center justify-between shadow-sm">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                          <Shield className="h-4 w-4 text-purple-600" />
                        </div>
                        <span className="text-sm text-gray-600 font-medium">ความมั่นใจ</span>
                      </div>
                      <span className="font-bold text-green-600">
                        {pendingConfirmation.confidence}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">!</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-yellow-900 mb-1">
                      กรุณาตรวจสอบข้อมูล
                    </p>
                    <p className="text-xs text-yellow-800 leading-relaxed">
                      หากข้อมูลถูกต้อง กดปุ่ม <span className="font-bold">"ยืนยันการลงเวลา"</span><br/>
                      หากไม่ใช่คุณ กดปุ่ม <span className="font-bold">"ไม่ใช่ฉัน"</span> เพื่อสแกนใหม่
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <button
                  onClick={cancelAndRescan}
                  disabled={isScanning}
                  className="bg-gradient-to-r from-red-500 to-orange-600 text-white py-4 px-4 rounded-2xl font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:transform-none flex flex-col items-center justify-center space-y-1"
                >
                  <X className="h-7 w-7" />
                  <span className="text-sm">ไม่ใช่ฉัน</span>
                </button>
                <button
                  onClick={confirmAttendance}
                  disabled={isScanning}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 text-white py-4 px-4 rounded-2xl font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:transform-none flex flex-col items-center justify-center space-y-1"
                >
                  {isScanning ? (
                    <>
                      <div className="w-7 h-7 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-sm">กำลังบันทึก...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-7 w-7" />
                      <span className="text-sm">ยืนยัน</span>
                    </>
                  )}
                </button>
              </div>

              <div className="text-center pt-2">
                <p className="text-xs text-gray-500">
                  🔒 ข้อมูลของคุณจะถูกบันทึกอย่างปลอดภัย
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 text-center shadow-lg border border-white/20">
            <Shield className={`h-6 w-6 mx-auto mb-2 ${modelsLoaded && !isWarmingUp ? 'text-green-500' : 'text-gray-400'}`} />
            <div className="text-xs font-medium text-gray-600">AI Model</div>
            <div className={`text-sm font-bold ${modelsLoaded && !isWarmingUp ? 'text-green-600' : 'text-gray-500'}`}>
              {!modelsLoaded ? 'Loading...' : 
               isWarmingUp ? 'Optimizing...' : 
               'Ready'}
            </div>
          </div>
          
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 text-center shadow-lg border border-white/20">
            <MapPin className={`h-6 w-6 mx-auto mb-2 ${wfhMode ? 'text-indigo-500' : (location && !location.loading && !location.error ? 'text-blue-500' : 'text-gray-400')}`} />
            <div className="text-xs font-medium text-gray-600">{wfhMode ? 'WFH Mode' : 'Location'}</div>
            <div className={`text-sm font-bold ${wfhMode ? 'text-indigo-600' : (location && !location.loading && !location.error ? 'text-blue-600' : 'text-gray-500')}`}>
              {wfhMode ? 'Active' : (location && location.loading ? 'Finding...' : 
               location && !location.loading && !location.error ? 'Ready' : 
               location && location.error ? 'Error' : 'Waiting')}
            </div>
          </div>
          
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 text-center shadow-lg border border-white/20">
            <Clock className="h-6 w-6 mx-auto mb-2 text-indigo-500" />
            <div className="text-xs font-medium text-gray-600">Time</div>
            <div className="text-sm font-bold text-indigo-600">
              {currentTime.toLocaleTimeString('th-TH', { 
                hour: '2-digit', 
                minute: '2-digit', 
                second: '2-digit' 
              })}
            </div>
          </div>
        </div>

        <div className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 overflow-hidden">
          
          <div className="relative bg-black rounded-t-3xl overflow-hidden" style={{ aspectRatio: '1/1' }}>
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className="w-full h-full object-cover"
              style={{ 
                display: cameraActive ? 'block' : 'none',
                transform: 'scaleX(-1)',
                objectPosition: 'center top'
              }}
              onLoadedMetadata={() => {
                if (videoRef.current) {
                  videoRef.current.play();
                }
              }}
            />
            
            <canvas 
              ref={canvasRef} 
              className="absolute inset-0 pointer-events-none"
              style={{ 
                display: cameraActive ? 'block' : 'none',
                transform: 'scaleX(-1)',
                objectPosition: 'center top'
              }}
            />
            
            <canvas 
              ref={captureCanvasRef} 
              style={{ display: 'none' }}
            />

            {/* [✅ เพิ่มใหม่] กล่องแจ้งเตือน Error ลอยอยู่บนกล้อง */}
            {scanError && (
              <div className="absolute top-4 left-4 right-4 bg-red-500/90 text-white px-4 py-3 rounded-2xl backdrop-blur-md flex items-center space-x-3 shadow-xl animate-in slide-in-from-top-4 duration-300 z-10">
                <AlertCircle className="h-6 w-6 flex-shrink-0 animate-pulse" />
                <div className="flex-1">
                  <p className="font-bold text-sm text-white">ตรวจไม่พบข้อมูล</p>
                  <p className="text-xs text-red-100">{scanError}</p>
                </div>
              </div>
            )}
            
            {!cameraActive && !result && !pendingConfirmation && (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-gray-800 to-gray-900">
                <div className="text-center text-white">
                  {/* [✅ อัปเดต] จัดโครงสร้างวงแหวนใหม่ให้ไม่ทับข้อความ */}
                  <div className="relative w-24 h-24 mx-auto mb-6">
                    {/* พื้นหลังไอคอนกล้อง */}
                    <div className="w-full h-full bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
                      <Camera className="h-12 w-12 text-white/70" />
                    </div>
                    
                    {/* วงแหวนหมุน (เกาะติดกับขอบ w-24 h-24 พอดี) */}
                    {isSystemPreparing && (
                      <div className="absolute -inset-2 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </div>
                  {/* [✅ แก้ไข] ปรับข้อความให้สมูทขึ้น */}
                  <h3 className="text-xl font-bold mb-2">
                    {isSystemPreparing ? 'กำลังเตรียมระบบ...' : 
                     (!wfhMode && location?.error) ? 'เกิดข้อผิดพลาด' : 
                     'พร้อมสแกนใบหน้า'}
                  </h3>
                  <p className="text-gray-300">
                    {!modelsLoaded ? 'กำลังโหลด AI Models...' :
                     isWarmingUp ? 'กำลังปรับแต่ง AI...' :
                     (!wfhMode && location?.loading) ? 'กำลังค้นหาพิกัด GPS...' :
                     (!wfhMode && location?.error) ? location.message || 'ไม่สามารถระบุตำแหน่งได้' :
                     'กำลังเปิดกล้องอัตโนมัติ...'
                    }
                  </p>
                </div>
              </div>
            )}

            {isScanning && (
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                <div className="text-center text-white">
                  <div className="relative mb-6">
                    <div className="w-20 h-20 border-4 border-green-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-2">
                    {pendingConfirmation ? 'กำลังบันทึก...' : 'Scanning Face...'}
                  </h3>
                  <p className="text-green-300 animate-pulse">Please hold still</p>
                </div>
              </div>
            )}

            {cameraActive && !isScanning && (
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-0 right-0 bottom-0 flex items-start justify-center pt-4">
                  <div className="w-2/4 h-3/5 border-2 border-white/50 rounded-3xl relative">
                    <div className="absolute -top-1 -left-1 w-8 h-8 border-l-4 border-t-4 border-green-400 rounded-tl-lg"></div>
                    <div className="absolute -top-1 -right-1 w-8 h-8 border-r-4 border-t-4 border-green-400 rounded-tr-lg"></div>
                    <div className="absolute -bottom-1 -left-1 w-8 h-8 border-l-4 border-b-4 border-green-400 rounded-bl-lg"></div>
                    <div className="absolute -bottom-1 -right-1 w-8 h-8 border-r-4 border-b-4 border-green-400 rounded-br-lg"></div>
                  </div>
                </div>
                
                <div className="absolute bottom-4 left-0 right-0 text-center">
                  <div className="bg-black/50 text-white px-4 py-2 rounded-full mx-auto inline-block backdrop-blur-sm">
                    <p className="text-sm font-medium">Position your face in the frame</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
            
            {!cameraActive && isManualStop && !result && !pendingConfirmation && (
              <button
                onClick={startCamera}
                disabled={!modelsLoaded || isWarmingUp || (!wfhMode && ((location && location.loading) || (location && location.error)))}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-4 px-6 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:transform-none disabled:shadow-lg flex items-center justify-center space-x-3"
              >
                <Camera className="h-6 w-6" />
                <span>
                  {!modelsLoaded ? 'Loading AI...' : 
                   isWarmingUp ? 'Optimizing AI...' :
                   wfhMode ? 'Start Camera (WFH)' :
                   (location && location.loading) ? 'Getting Location...' : 
                   (location && location.error) ? 'Location Error' :
                   'Start Camera'}
                </span>
              </button>
            )}

            {!cameraActive && !result && !pendingConfirmation && !wfhMode && location && location.error && (
              <button
                onClick={loadCurrentLocation}
                className="w-full bg-orange-500 text-white py-3 px-6 rounded-2xl font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <MapPin className="h-5 w-5" />
                <span>รีเฟรชตำแหน่ง</span>
              </button>
            )}
            
            {/* ปุ่มหยุดกล้องเผื่อต้องการยกเลิก (เป็น Option ซ่อนไว้มุมๆ หรือจะเอาออกเลยก็ได้) */}
            {cameraActive && !isScanning && !pendingConfirmation && !result && (
              <button
                  onClick={stopCamera}
                  className="w-full mt-3 text-gray-500 text-sm hover:text-gray-700 font-medium"
              >
                ปิดกล้องและยกเลิก
              </button>
            )}

            {result && (
              <div className="space-y-4">
                <div className={`p-6 rounded-2xl ${result.success ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
                  <div className={`flex items-center space-x-3 ${result.success ? 'text-green-800' : 'text-red-800'}`}>
                    {result.success ? (
                      <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                        <CheckCircle className="h-7 w-7 text-white" />
                      </div>
                    ) : (
                      <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                        <AlertCircle className="h-7 w-7 text-white" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-lg font-bold">{result.message || result.error}</p>
                      {result.success && result.employee && (
                        <div className="mt-3 space-y-1">
                          <div className="flex items-center space-x-2">
                            <Users className="h-4 w-4" />
                            <span className="font-medium">{result.employee.first_name} {result.employee.last_name}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${result.action === 'check_in' ? 'bg-blue-500' : 'bg-orange-500'}`}></div>
                            <span className="text-sm">{result.action === 'check_in' ? 'เข้างาน' : 'ออกงาน'}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Clock className="h-4 w-4" />
                            <span className="text-sm">{new Date().toLocaleString('th-TH')}</span>
                          </div>
                          {result.confidence && (
                            <div className="flex items-center space-x-2">
                              <Shield className="h-4 w-4" />
                              <span className="text-sm">ความมั่นใจ: {result.confidence}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {!result.success && (
                        <div className="mt-2 text-sm">
                          {result.distance && (
                            <p>ระยะห่าง: {result.distance} (สูงสุด: {result.max_distance})</p>
                          )}
                          {result.registered_faces !== undefined && (
                            <p>จำนวนพนักงานในระบบ: {result.registered_faces} คน</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                  <button
                    onClick={startCamera}
                    disabled={!modelsLoaded || isWarmingUp || (!wfhMode && ((location && location.loading) || (location && location.error)))}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-4 px-6 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:transform-none disabled:shadow-lg flex items-center justify-center space-x-3"
                  >
                    <Camera className="h-6 w-6" />
                    <span>
                      {!modelsLoaded ? 'Loading AI...' : 
                      isWarmingUp ? 'Optimizing AI...' :
                      wfhMode ? 'Start Camera (WFH)' :
                      (location && location.loading) ? 'Getting Location...' : 
                      (location && location.error) ? 'Location Error' :
                      'Scan Again'}
                    </span>
                  </button>

              </div>
            )}
          </div>
        
        {/* ... (ส่วนแสดง Location และ WFH Mode เหมือนเดิม) ... */}
        {!wfhMode && location && !location.loading && !location.error && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20">
            <div className="flex items-center space-x-3 text-sm">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <MapPin className="h-4 w-4 text-blue-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900">Current Location</p>
                <p className="text-gray-600 truncate">
                  {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                  {location.accuracy && (
                    <span className="text-gray-500"> (±{Math.round(location.accuracy)}m)</span>
                  )}
                </p>
              </div>
              <div className="flex-shrink-0">
                <div className={`w-3 h-3 rounded-full ${
                  location.accuracy <= 50 ? 'bg-green-500' : 
                  location.accuracy <= 100 ? 'bg-yellow-500' : 
                  'bg-red-500'
                }`}></div>
              </div>
            </div>
            {location.accuracy > 50 && (
              <div className="mt-3 p-3 bg-yellow-50 rounded-xl border border-yellow-200">
                <p className="text-xs text-yellow-700 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {location.accuracy > 100 ? 
                    'ความแม่นยำของตำแหน่งต่ำ กรุณาไปที่บริเวณโล่ง' : 
                    'ความแม่นยำปานกลาง แนะนำให้ไปที่บริเวณโล่ง'}
                </p>
              </div>
            )}
          </div>
        )}

        {!wfhMode && location && location.loading && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">Finding your location...</p>
                <p className="text-sm text-gray-600">Please wait while we get your GPS coordinates</p>
              </div>
            </div>
          </div>
        )}

        {!wfhMode && location && location.error && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-red-200">
            <div className="flex items-center space-x-3">
              <AlertCircle className="h-6 w-6 text-red-500" />
              <div className="flex-1">
                <p className="font-medium text-red-900">ไม่สามารถรับตำแหน่งได้</p>
                <p className="text-sm text-red-700">{location.message}</p>
                <p className="text-xs text-red-600 mt-1">กรุณาเปิด GPS และอนุญาตให้เบราว์เซอร์เข้าถึงตำแหน่ง</p>
              </div>
            </div>
          </div>
        )}

        {wfhMode && (
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-4 shadow-lg border border-indigo-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center">
                <Camera className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-indigo-900">โหมด Work From Home</p>
                <p className="text-sm text-indigo-700">ไม่ต้องตรวจสอบตำแหน่ง GPS</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div 
        className="fixed bottom-4 right-4 bg-gray-900/90 text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-2xl cursor-pointer hover:bg-gray-700 transition-colors z-50"
      >
        {CURRENT_VERSION}
      </div>
    </div>
  );
};

export default FaceScanner;